import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormControl, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { StateService } from '../../services/state.service';
import { FreemiumService } from '../../services/freemium.service';
import { RegisterTrialEmailComponent } from './register-trial-email/register-trial-email.component';
import { TrialStepTwoComponent } from './trial-step-two/trial-step-two.component';
import { TrialStepThreeComponent } from './trial-step-three/trial-step-three.component';
import { TrialStepFourComponent } from './trial-step-four/trial-step-four.component';
import { TrialStepIndicatorComponent } from './trial-step-indicator/trial-step-indicator.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, map } from 'rxjs';
import { DestroyRef } from '@angular/core';
import { SelectItem } from '../../shared/components/select-menu/select-menu.component';
import { TokenService } from '../../services/token.service';
import { AssetVersionPipe } from '../../pipes/asset-version.pipe';
import { RoutePaths, TrialStep } from '../../shared/types/common';
import { HubspotFirstPhase, HubspotFormPhase, HubspotUtm } from '../../services/hubspot/hubspot.types';
import { GetSignupResponse } from '../../shared/types/freemium.types';
import { AUTH0_ROLE, UserProfileResponse } from '../../shared/types/UserProfile';
import { PosthogService } from '../../services/posthog.service';
import { PosthogEventName } from '../../services/types';
import { GtmService } from '../../services/gtm.service';
import { CustomValidators } from '../../shared/validators';

@Component({
  selector: 'app-trial-screen',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RegisterTrialEmailComponent,
    TrialStepTwoComponent,
    TrialStepThreeComponent,
    TrialStepFourComponent,
    TrialStepIndicatorComponent,
    AssetVersionPipe
  ],
  templateUrl: './trial-screen.component.html',
  styleUrl: './trial-screen.component.scss',
})
export class TrialScreenComponent implements OnInit {
  private stateService = inject(StateService);
  private freemiumService = inject(FreemiumService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private tokenService = inject(TokenService);
  private posthogService = inject(PosthogService);
  private gtmService = inject(GtmService);
  
  public isGoogleSignupFlow = false;
  public isGoogleRegisteredUser = false;
  
  public currentStep = TrialStep.REGISTER_EMAIL;
  public readonly TrialStep = TrialStep;

  firstNameControl = new FormControl('', [Validators.required]);
  lastNameControl = new FormControl('', [Validators.required]);
  emailControl = new FormControl('', [
    Validators.required,
    this.emailFormatValidator.bind(this),
    CustomValidators.businessEmail()
  ]);

  companyNameControl = new FormControl<string | null>(null, [Validators.required]);
  jobTitleControl = new FormControl<string | null>(null, [Validators.required]);

  jobTitleItems: SelectItem<string>[] = [];

  ngOnInit(): void {
    this.gtmService.init();
    this.loadSignupOptions();
    this.setIsGoogleRegisteredUser();
    this.route.queryParams.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.stateService.loading = true;
      this.checkQueryParams();
    });
  }

  private loadSignupOptions(): void {
    this.freemiumService.getSignupOptions()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.jobTitleItems = response.jobTitles.map(title => ({
            label: title,
            key: title
          }));
        },
        error: (error) => {
          console.error('Error loading signup options:', error);
          this.jobTitleItems = [];
        }
      });
  }

  private setIsGoogleRegisteredUser(): void {
    const queryParams = this.route.snapshot.queryParams;
    this.isGoogleRegisteredUser = queryParams['id']?.includes('google-oauth2');
  }

  private checkQueryParams(): void {
    const queryParams = this.route.snapshot.queryParams;

    // Google signup
    const user = queryParams['user'];
    const token = queryParams['token'];

    // Email signup callback
    const email = queryParams['email']?.replace(/\s/g, '');
    const signupToken = queryParams['signupToken'];
    const id = queryParams['id'];

    const isGoogleSignup = user && token;
    const isEmailSignup = email && signupToken && id;

    if (isGoogleSignup) {
      this.processGoogleSignup(user, token);
    }
    else if (isEmailSignup) {
      const utms: HubspotUtm = {
        utm_source: queryParams['utm_source'],
        utm_medium: queryParams['utm_medium'],
        utm_campaign: queryParams['utm_campaign'],
        utm_content: queryParams['utm_content'],
        content_variant: queryParams['content_variant']
      }
      this.processEmailSignup(email, signupToken, id, utms);
    } else {
      this.stateService.loading = false;
    }
  }

  private processGoogleSignup(user: string, token: string): void {
    this.isGoogleSignupFlow = true;
    const userData = JSON.parse(user) as UserProfileResponse;
    if (userData.isNewUser) {
      this.processNewGoogleUser(userData.id, userData.email, userData.firstName, userData.lastName);
    }
    this.processUserResponse(userData, token);
    this.stateService.loading = false;
  }

  private processNewGoogleUser(id: string, email: string, firstName: string, lastName: string): void {
    const hubspotPayload: HubspotFirstPhase = { email: email, firstname: firstName, lastname: lastName, userId: id };
    this.freemiumService.updateHubspotFields({ email: email, firstname: firstName, lastname: lastName });
    this.freemiumService.sendToHubSpot(HubspotFormPhase.STEP_ONE, hubspotPayload, window.location.href) 
      .catch(error => console.error('Error sending to HubSpot:', error));
    this.posthogService.identify({
      id: id,
      email: email,
      firstName: firstName,
      lastName: lastName,
      role: AUTH0_ROLE.TRIAL,
      account: 'temp',
      trialDaysLeft: 14
    })
    this.posthogService.capture(PosthogEventName.SIGN_UP_SUCCEEDED, {'sign_up_method': 'google_sso'});
  }

  private processEmailSignup(email: string, signupToken: string, id: string, utms?: Partial<HubspotUtm>): void {
    this.freemiumService.getTrialUser(email, signupToken, id)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => { this.stateService.loading = false; })
      )
      .subscribe({
        next: (response) => {
          this.processUserResponse(response, response.token.access_token, utms);
        },
        error: () => {
          this.emailControl.setValue(email);
          this.currentStep = TrialStep.REGISTER_EMAIL;
          this.router.navigate([], { relativeTo: this.route, queryParams: {...utms}, replaceUrl: true });
        }
      });
  }

  private processUserResponse(response: GetSignupResponse | UserProfileResponse, token?: string, utms?: Partial<HubspotUtm>): void {
    const hubspotFields = {};
    const { firstName, lastName, email, companyName, jobTitle } = response;
    const accountKey = 'accountKey' in response ? (response as GetSignupResponse).accountKey : (response as UserProfileResponse).account?.key;

    if (token) {
      this.tokenService.setToken(token);
    }
    this.firstNameControl.setValue(firstName);
    this.lastNameControl.setValue(lastName);
    this.emailControl.setValue(email);

    Object.assign(hubspotFields, { email, firstName, lastName });

    if (companyName && jobTitle) {
      this.companyNameControl.setValue(companyName);
      this.jobTitleControl.setValue(jobTitle);
      this.currentStep = TrialStep.COMPLETE;
      Object.assign(hubspotFields, { company: companyName, jobtitle: jobTitle })
    }
    else if (accountKey) {
      this.currentStep = TrialStep.PROFILE;
    }
    else if (email) {
      this.currentStep = TrialStep.SELECT_BRAND;
    }

    this.freemiumService.updateHubspotFields(hubspotFields);
    
    this.router.navigate([], { relativeTo: this.route, queryParams: {...utms}, replaceUrl: true });
    
    this.redirectToHomeIfGoogleSignupComplete();
  }

  private emailFormatValidator(control: AbstractControl): ValidationErrors | null {
    const email = control.value;
    if (!email) return null;

    // Pattern: string@string.string (requires domain with TLD)
    const emailPattern = /^[^@]+@[^@]+\.[^@]+$/;
    if (!emailPattern.test(email)) {
      return { email: true };
    }
    return null;
  }

  private navigateToHomePage(): void {
    this.router.navigate([RoutePaths.POLARIS]);
  }

  private redirectToHomeIfGoogleSignupComplete(): void {
    if (this.isGoogleSignupFlow && this.currentStep === TrialStep.COMPLETE) {
      this.navigateToHomePage();
    }
  }

  public onNextStep(): void {
    if (this.currentStep < TrialStep.COMPLETE) {
      this.currentStep++;
    }
    this.redirectToHomeIfGoogleSignupComplete();
  }

  public onGoogleSignupSuccessed(): void {
    this.isGoogleSignupFlow = true;
    this.navigateToHomePage();
  }
}
